import React from 'react';
import { ExternalLink, Cloud, Snowflake } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Apps: React.FC = () => {
  const apps = [
    {
      name: 'Roam Weather',
      description: 'Get comprehensive multi-day weather forecasts for multiple locations simultaneously. Perfect for planning climbing trips, hiking adventures, or any outdoor activity.',
      status: 'Available',
      url: '/weather',
      isInternalLink: true,
      icon: Cloud,
      features: [
        'Multi-location forecasts',
        'Extended 7-day predictions',
        'Smoke & air quality forecasts',
        'Mobile-friendly design'
      ],
      gradient: 'from-blue-500 to-cyan-500'
    },

    {
      name: 'Roam Office Tracker',
      description: 'Track your office days and meet your "3 days a week" requirement. Plan when you can work remotely and see how long you can be gone while staying compliant.',
      status: 'Available',
      url: 'https://office.roamapps.com',
      iconUrl: 'https://office.roamapps.com/icon.svg',
      features: [
        'Configurable "best 8 of 12 weeks" compliance tracking',
        'Calendar-based office day planning',
        'Remote work schedule visibility',
        'Fully configurable requirements'
      ],
      gradient: 'from-orange-500 to-amber-500'
    },

    {
      name: 'Roam Shopping',
      description: 'Save products while browsing, mark your interest level, and add notes so you never start your research over. Works offline with no account required.',
      status: 'Available',
      url: '/shopping',
      iconUrl: '/shopping-icon.png',
      fullIcon: true,
      isInternalLink: true,
      features: [
        'Save & annotate products while browsing',
        'Mark interest as No, Maybe, Yes',
        'Recognize already-considered items in search results',
        'Works offline, no account required',
        'Optional sync when signed in'
      ],
      gradient: 'from-purple-500 to-violet-500'
    },
    {
      name: 'Roam Powder',
      description: 'See cumulative snow forecasts for any point location of your choice. Plan when you should go skiing for the best powder conditions and never miss epic snow days.',
      status: 'Coming Soon',
      url: '#',
      icon: Snowflake,
      features: [
        'Cumulative snow forecasts',
        'Point location selection'
      ],
      gradient: 'from-indigo-500 to-purple-500'
    }
  ];

  return (
    <section id="apps" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Our Adventure Apps
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover tools designed specifically for outdoor enthusiasts, 
            from casual hikers to serious mountaineers.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {apps.map((app, index) => {
            const IconComponent = app.icon;
            return (
              <div key={index} className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    {(app as any).fullIcon ? (
                      <img src={app.iconUrl} alt={`${app.name} icon`} className="h-14 w-14 rounded-2xl" />
                    ) : (
                      <div className={`bg-gradient-to-r ${app.gradient} p-3 rounded-xl`}>
                        {app.iconUrl ? (
                          <img src={app.iconUrl} alt={`${app.name} icon`} className="h-8 w-8" />
                        ) : IconComponent ? (
                          <IconComponent className="h-8 w-8 text-white" />
                        ) : null}
                      </div>
                    )}
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      app.status === 'Available' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {app.status}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{app.name}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{app.description}</p>

                  <div className="mb-8">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      {app.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-600">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {app.status === 'Available' ? (
                    (app as any).isInternalLink ? (
                      <Link
                        to={app.url}
                        className={`w-full bg-gradient-to-r ${app.gradient} hover:opacity-90 text-white px-6 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all`}
                      >
                        Learn More
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    ) : (
                    <a 
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full bg-gradient-to-r ${app.gradient} hover:opacity-90 text-white px-6 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all`}
                    >
                      Launch App
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    )
                  ) : (
                    <button
                      disabled
                      className="w-full bg-gray-100 text-gray-500 px-6 py-3 rounded-xl font-medium cursor-not-allowed"
                    >
                      Coming Soon
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
